"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader as Loader2, CircleCheck as CheckCircle2, CircleAlert as AlertCircle } from "lucide-react";
const SERVICE_OPTIONS = ["Forestry Mulching", "Land Clearing", "Path & Trail Clearing", "Driveway Clearing", "Brush Removal", "Lot Preparation", "Overgrowth Removal"];
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <Alert className="bg-green-50 border-green-200 text-green-900">
        <CheckCircle2 className="size-5 text-green-600" />
        <AlertTitle className="font-default font-semibold text-green-800">Success!</AlertTitle>
        <AlertDescription className="mt-2">
          {successMessage}
          <Button onClick={resetForm} variant="outline" className="mt-4 block border-green-300 text-green-700 hover:bg-green-100">
            Send Another Message
          </Button>
        </AlertDescription>
      </Alert>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      {submitError && <Alert variant="destructive">
          <AlertCircle className="size-5" />
          <AlertTitle className="font-default font-semibold">Error</AlertTitle>
          <AlertDescription>{submitError}</AlertDescription>
        </Alert>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-secondary font-semibold">Your Name *</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className="bg-background" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-secondary font-semibold">Email Address *</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="bg-background" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-secondary font-semibold">Phone Number</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="(555) 123-4567" className="bg-background" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="budget" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-secondary font-semibold">Estimated Acreage / Size (sq ft)</FormLabel>
            <FormControl>
              <Input type="number" placeholder="2000" className="bg-background" {...field} onChange={e => field.onChange(e.target.value === "" ? "" : Number(e.target.value))} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="interests" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-secondary font-semibold">Which Services Do You Need?</FormLabel>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-2">
              {SERVICE_OPTIONS.map((service, i) => {
          const isChecked = Array.isArray(field.value) && field.value.includes(service);
          return <label key={service} data-index={i} className="flex items-center gap-2.5 p-3 rounded-lg border border-border bg-background/50 hover:bg-muted/50 cursor-pointer text-sm font-medium transition-colors">
                    <input type="checkbox" checked={isChecked} onChange={e => {
              const currentValues = Array.isArray(field.value) ? [...field.value] : [];
              if (e.target.checked) {
                field.onChange([...currentValues, service]);
              } else {
                field.onChange(currentValues.filter((val: string) => val !== service));
              }
            }} className="size-4 rounded border-border text-primary accent-primary focus:ring-primary" />
                    <span className="text-foreground">{service}</span>
                  </label>;
        })}
            </div>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="additional_details" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-secondary font-semibold">Project Details or Special Requests</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about the land clearing or mulching services you need..." className="min-h-[120px] bg-background resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <Button type="submit" disabled={isSubmitting} size="lg" className="w-full md:w-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-md hover:shadow-lg transition-all duration-200 font-default font-semibold text-base px-8">
        {isSubmitting ? <>
            <Loader2 className="mr-2 size-5 animate-spin" />
            Sending Request...
          </> : "Get a Free Quote"}
      </Button>
    </Form>;
}
export default function ContactForm() {
  return <section id="contactform" className="bg-background py-16 md:py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold text-secondary mb-4 tracking-tight">
            Get in Touch
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Ready to transform your property? Fill out the form below with your project details, and our team will schedule a site visit with you so we can provide an accurate quote for your project.
          </p>
        </div>

        <div id="contact-form" className="bg-card rounded-2xl shadow-xl border border-border p-6 md:p-10">
          <FormProvider formId="start_project_form_home" sectionName="ContactForm" defaultValues={{
          budget: 2000,
          interests: [],
          name: "",
          email: "",
          phone: "",
          additional_details: ""
        }} validationRules={{
          name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          }
        }} submitText="Get a Free Quote" successMessage="Thank you for reaching out! We have received your request and will contact you shortly.">
            <ContactFormContent />
          </FormProvider>
        </div>
      </div>
    </section>;
}